import angular from "angular";
import loginFormTemplate from './loginForm.html';
import './login.css';

/* @ngInject */ export function LoginFormAppRoutesProvider(AppRoutesProvider, Routes) {
  angular.extend(this, AppRoutesProvider);

  /*****************************************************************************
   * login */
  /**
   * @ngdoc      property
   * @name       login
   * @propertyOf app.object:AppRoutesProvider
   *
   * @description
   * The route definition object for the login route.
   *
   * This corresponds to the route: "/login/:registered?"
   *****************************************************************************/
  this.login = { name: Routes.login, template: '<login-form>' };
}

/*******************************************************************************
 * loginForm */
/**
 * @ngdoc    component
 * @module   app
 * @name     app.component:loginForm
 *
 * @description
 * This component handles the authentication input as well as the start of the
 * forgot password.
 *******************************************************************************/
export const loginFormComponent = {
  template: loginFormTemplate,
  controller: Controller
};

/*******************************************************************************
 * Controller
 *******************************************************************************/
/* @ngInject */ function Controller($injector) {
  var $svcs = { $ctrl: this, $globals: {} };

  $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs }));
  $injector.invoke(initGlobal, null, $svcs);
}

/*******************************************************************************
 * initCtrl */
/**
 * Initializes the Controller object.
 *******************************************************************************/
/* @ngInject */ function initCtrl($ctrl, $injector, $svcs, BusyScreen) {
  BusyScreen.setBusy(true);
  Object.assign($ctrl, {
    heading: 'Please Login',
    impersonate: false,
    showError: false,
    errorMessage: null,
    username: null,
    password: null,

    $onInit: $injector.invoke(onInit, null, $svcs),
    submit: $injector.invoke(submit, null, $svcs)
  });
}

/*******************************************************************************
 * initGlobals */
/**
 * Initializes the Globals object.
 *******************************************************************************/
/* @ngInject */ function initGlobal($globals, $injector, $svcs) {
  Object.assign($globals, {
    currentUser: null,

    authenticate: $injector.invoke(authenticate, null, $svcs),
    doImpersonation: $injector.invoke(doImpersonation, null, $svcs)
  });
}

/*******************************************************************************
 * Global.authenticate */
/**
 * Performs a credential check to verify the user. If the user has access to
 * multiple organizations, then a panel will be shown to select a site to log
 * into.
 *
 * @param   un   Username
 * @param   pw   Password
 * @param   org  Organization ID to log into. Optional parameter.
 * @returns      Promise
 *******************************************************************************/
/* @ngInject */ function authenticate(
  $ctrl,
  $globals,
  $q,
  BusyScreen,
  CommonSvc,
  DisplayLicenseDialog,
  OrgSelectDialog
) {
  /*------------------------------------------------------------*/
  /* If there was an error, set the message and show the error. */
  /*------------------------------------------------------------*/
  function handleError(error) {
    $ctrl.showError = true;
    $ctrl.errorMessage = error.parsedResponse.message();
  }

  /*---------------------------------------------------------------*/
  /* Sends the user to the main application through the entryPath. */
  /*---------------------------------------------------------------*/
  function redirect(entryPath) {
    window.location.href = entryPath;
  }

  return function (un, pw, orgID) {
    CommonSvc.authenticateUser(un, pw, orgID)
      .then(function (resp) {
        const data = resp.data;
        const impersonating = $globals.doImpersonation(data.user);

        if (!impersonating) {
          /*------------------------------------------------*/
          /* Show the EULA if the user had not accepted it. */
          /*------------------------------------------------*/
          const showLicense =
            !data.hasLicense && !data.user.impersonate
              ? DisplayLicenseDialog.show()
              : $q.resolve();

          showLicense.then(() => {
            /*------------------------------------------------*/
            /* If there is an org list, user must select the  */
            /* organization and recurse through authenticate. */
            /*------------------------------------------------*/
            if (data.orgList && data.orgList.length > 0) {
              OrgSelectDialog.show(data.orgList, function (chosenOrgID) {
                $globals.authenticate(un, pw, chosenOrgID);
              });
              /*------------------------------------------------*/
              /* Login successful. Return the users entry path. */
              /*------------------------------------------------*/
            } else {
              /**
               * ! We do not need to check for OrgID because at this point an Org has been selected
               * ! so we always have an OrgID.
               *
               * We remove the menu for this org from session storage to ensure that the menu is completely
               * fresh on each login. This is accomodate for any Access Control menu changes that could have occured
               * from a release while the user is using the application.
               */
              var menuKey = Object.keys(sessionStorage).find(
                (key) =>
                  key.includes('menu-') && key.includes(`orgID:${orgID}`)
              );

              if (menuKey) {
                sessionStorage.removeItem(menuKey);
              }

              redirect(data.entryPath);
            }
          });
        }
      })
      .catch(handleError)
      .finally(() => BusyScreen.setBusy(false));
  };
}

/*******************************************************************************
 * Global.doImpersonation */
/**
 * If the user is impersonating another, set focus to username field to get
 * username to impersonate, and reauthorize.
 *******************************************************************************/
/* @ngInject */ function doImpersonation($ctrl, $window) {
  return function (user) {
    if (user.impersonate && user.userID == 0) {
      $ctrl.impersonate = true;
      $ctrl.heading = 'Impersonate User';
      $ctrl.username = '';

      $window.document.getElementById('username').focus();
      return true;
    }
  };
}

/*******************************************************************************
 * Controller.onInit */
/**
 * Angular event that fires when the component is ready.
 *******************************************************************************/
/* @ngInject */ function onInit(BusyScreen, Routes) {
  return function () {
    /*---------------------------------------*/
    /* Split the queryString out of the url. */
    /*---------------------------------------*/
    const queryString = new URLSearchParams(window.location.search);

    /*------------------------------------------------------*/
    /* If we have a code in the query string, then this is  */
    /* coming from an OAuth redirect. Send this code to the */
    /* server to try to find a user to log in with.         */
    /*------------------------------------------------------*/
    if (queryString.size > 0) {
      const state = queryString.get('state');
      const code = queryString.get('code');

      /*------------------------------------------------------*/
      /* Rebuild the href to remove the query string, and     */
      /* add the oauth properties needed to the route params. */
      /*------------------------------------------------------*/
      window.location.href = `${window.location.origin}${window.location.pathname}${window.location.hash}${Routes.oauth}/${state}/${code}`;
    } else BusyScreen.setBusy(false);

    /*--------------------------------------*/
    /* If we aren't coming back from oauth, */
    /*  we can remove the BusyScreen.       */
    /*--------------------------------------*/
  };
}

/*******************************************************************************
 * Controller.submit */
/**
 * Click handler for the Login button
 * Submits the user credentials for authentication
 *******************************************************************************/
/* @ngInject */ function submit($ctrl, $globals) {
  return function () {
    /*--------------------------------*/
    /* Display error on login failure */
    /*--------------------------------*/
    $ctrl.showError = false;
    $ctrl.errorMessage = null;

    $globals.authenticate($ctrl.username, $ctrl.password, null);
  };
}